import React, { useState, useEffect, useCallback } from "react";
import {
  Box, CircularProgress, Typography, Paper, Grid, TextField, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TablePagination,
  IconButton, Tooltip, Select, MenuItem, FormControl, InputLabel, Alert, Dialog,
  DialogActions, DialogContent, DialogContentText, DialogTitle,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { motion } from "framer-motion";
import { Header } from "components/shared";
import { getOperations, createOperation, updateOperation, deleteOperation, getAllUsers } from "utils/api";
import { format } from "date-fns";
import { Operation, OperationKind } from "utils/types/operationsTypes";
import { User } from "utils/types/UserTypes";
import { useUtility } from "utils/context/UtilityContext";
import { useDebounce } from "utils/hooks/useDebounce";

// --- Reusable Components ---

const DeleteConfirmDialog = ({ open, onClose, onConfirm }: { open: boolean; onClose: () => void; onConfirm: () => void; }) => (
    <Dialog open={open} onClose={onClose}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent><DialogContentText>Are you sure you want to delete this manual operation? This action cannot be undone.</DialogContentText></DialogContent>
        <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button onClick={onConfirm} color="error">Delete</Button>
        </DialogActions>
    </Dialog>
);

const OperationModal = ({ open, onClose, onSave, operation, users }: { open: boolean; onClose: () => void; onSave: (data: any) => void; operation: Operation | null; users: User[] }) => {
    const [formData, setFormData] = useState({ amount: '', description: '', country: '', userId: '' });

    useEffect(() => {
        if (operation) {
            setFormData({
                amount: (operation.revenue - operation.cost).toString(),
                description: operation.description,
                country: operation.country || '',
                userId: operation.userId || '',
            });
        } else {
            setFormData({ amount: '', description: '', country: '', userId: '' });
        }
    }, [operation, open]);

    const handleSave = () => {
        onSave({ id: operation?.id, ...formData });
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{operation ? 'Edit' : 'Add'} Manual Operation</DialogTitle>
            <DialogContent>
                <TextField autoFocus margin="dense" label="Amount (use negative for withdrawal)" type="number" fullWidth value={formData.amount} onChange={e => setFormData({ ...formData, amount: e.target.value })} />
                <TextField margin="dense" label="Description" type="text" fullWidth multiline rows={3} value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} />
                <TextField margin="dense" label="Country Code (e.g., US, FR, CD)" type="text" fullWidth value={formData.country} onChange={e => setFormData({ ...formData, country: e.target.value.toUpperCase() })} />
                <FormControl fullWidth margin="dense">
                    <InputLabel>Target User (Optional)</InputLabel>
                    <Select label="Target User (Optional)" value={formData.userId} onChange={e => setFormData({ ...formData, userId: e.target.value })}>
                        <MenuItem value=""><em>None</em></MenuItem>
                        {/* This line causes the error if 'users' is not an array */}
                        {Array.isArray(users) && users.map(user => <MenuItem key={user.userID} value={user.userID}>{user.username} ({user.email})</MenuItem>)}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained">Save</Button>
            </DialogActions>
        </Dialog>
    );
};


// --- Main Page Component ---

export const OperationsPage = () => {
  const { marginBottomHeader } = useUtility();
  const [operations, setOperations] = useState<Operation[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [filters, setFilters] = useState({ kind: 'Transactions' as OperationKind | '', search: '' });
  const [pagination, setPagination] = useState({ page: 0, limit: 10, total: 0 });
  const [sort, setSort] = useState({ sortBy: 'date', order: 'desc' as 'asc' | 'desc' });

  const debouncedSearch = useDebounce(filters.search, 500);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOperation, setSelectedOperation] = useState<Operation | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const fetchUsers = async () => {
      try {
          let usersData = await getAllUsers();
          // FIX: Ensure usersData is an array. If the API returns an object, convert it.
          if (usersData && !Array.isArray(usersData)) {
              usersData = Object.values(usersData);
          }
          setUsers(usersData || []); // Set to empty array if null/undefined
      } catch (err) {
          console.error("Failed to fetch users:", err);
          setUsers([]); // Ensure users is an array even on error
      }
  };

  const fetchOperations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { 
          page: pagination.page + 1, 
          limit: pagination.limit, 
          kind: filters.kind || undefined,
          search: debouncedSearch || undefined,
          sortBy: sort.sortBy,
          order: sort.order,
      };
      const response = await getOperations(params);
      setOperations(response.data);
      setPagination(prev => ({ ...prev, total: response.meta.total }));
    } catch (err: any) {
      setError(err.message || "Failed to load data.");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters.kind, debouncedSearch, sort]);

  useEffect(() => {
    fetchUsers();
    fetchOperations();
  }, [fetchOperations]);

  const handleSort = (property: string) => {
    const isAsc = sort.sortBy === property && sort.order === 'asc';
    setSort({ sortBy: property, order: isAsc ? 'desc' : 'asc' });
  };

  const handleSaveOperation = async (data: any) => {
      try {
          if (data.id) {
              await updateOperation(data.id, { amount: parseFloat(data.amount), description: data.description, country: data.country });
          } else {
              await createOperation({ amount: parseFloat(data.amount), description: data.description, country: data.country, userId: data.userId });
          }
          fetchOperations(); // Refresh data
      } catch (err: any) {
          setError(err.message || "Failed to save operation.");
      }
  };

  const handleDelete = async () => {
      if (!selectedOperation) return;
      try {
          await deleteOperation(selectedOperation.id);
          setDeleteConfirmOpen(false);
          setSelectedOperation(null);
          fetchOperations(); // Refresh data
      } catch (err: any) {
          setError(err.message || "Failed to delete operation.");
      }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className={`${marginBottomHeader} p-4`}>
      <Header secondPart={false} />
      <Box sx={{ width: '100%', p: { xs: 1, md: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4">Operations Log</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setSelectedOperation(null); setModalOpen(true); }}>
                Add Manual Operation
            </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                        <InputLabel>Category</InputLabel>
                        <Select value={filters.kind} label="Category" onChange={e => setFilters({ ...filters, kind: e.target.value as OperationKind | '' })}>
                            <MenuItem value="Transactions">Transactions</MenuItem>
                            <MenuItem value="Operations">Operations & Benefits</MenuItem>
                            <MenuItem value="Promotions">Promotions</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={8}>
                    <TextField fullWidth label="Search..." value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })} />
                </Grid>
            </Grid>
        </Paper>

        <Paper>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sortDirection={sort.sortBy === 'date' ? sort.order : false}><TableSortLabel active={sort.sortBy === 'date'} direction={sort.order} onClick={() => handleSort('date')}>Date</TableSortLabel></TableCell>
                            <TableCell><TableSortLabel active={sort.sortBy === 'type'} direction={sort.order} onClick={() => handleSort('type')}>Type</TableSortLabel></TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell align="right"><TableSortLabel active={sort.sortBy === 'revenue'} direction={sort.order} onClick={() => handleSort('revenue')}>Revenue</TableSortLabel></TableCell>
                            <TableCell align="right"><TableSortLabel active={sort.sortBy === 'cost'} direction={sort.order} onClick={() => handleSort('cost')}>Cost</TableSortLabel></TableCell>
                            <TableCell align="right"><TableSortLabel active={sort.sortBy === 'profit'} direction={sort.order} onClick={() => handleSort('profit')}>Profit</TableSortLabel></TableCell>
                            <TableCell>Country</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={8} align="center"><CircularProgress /></TableCell></TableRow>
                        ) : operations.map(op => (
                            <TableRow key={op.id}>
                                <TableCell>{format(new Date(op.date), 'Pp')}</TableCell>
                                <TableCell>{op.type}</TableCell>
                                <TableCell>{op.description}</TableCell>
                                <TableCell align="right">${op.revenue.toFixed(2)}</TableCell>
                                <TableCell align="right">${op.cost.toFixed(2)}</TableCell>
                                <TableCell align="right" sx={{ color: op.profit >= 0 ? 'green' : 'red' }}>${op.profit.toFixed(2)}</TableCell>
                                <TableCell>{op.country}</TableCell>
                                <TableCell>
                                    {(op.type === 'Manual Recharge' || op.type === 'Manual Withdrawal') && (
                                        <>
                                        <Tooltip title="Edit"><IconButton onClick={() => { setSelectedOperation(op); setModalOpen(true); }}><EditIcon /></IconButton></Tooltip>
                                        <Tooltip title="Delete"><IconButton onClick={() => { setSelectedOperation(op); setDeleteConfirmOpen(true); }}><DeleteIcon color="error"/></IconButton></Tooltip>
                                        </>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={pagination.total}
                rowsPerPage={pagination.limit}
                page={pagination.page}
                onPageChange={(e, newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
                onRowsPerPageChange={e => setPagination({ page: 0, limit: parseInt(e.target.value, 10), total: 0 })}
            />
        </Paper>
      </Box>
      <OperationModal open={modalOpen} onClose={() => setModalOpen(false)} onSave={handleSaveOperation} operation={selectedOperation} users={users} />
      <DeleteConfirmDialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} onConfirm={handleDelete} />
    </motion.div>
  );
};

